import { Alert, Box } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';
import { Route, RouteProps } from 'react-router-dom';

import { HandleCompanyPreferences } from '~/components/CompanyPreferences';
import { SelectedEmployeeContextRootProvider } from '~/components/SelectedEmployee';
import { useSessionInitialize } from '~/components/SessionMonitor/useSessionMonitor';
import { HandleUserSettings } from '~/components/UserSettings';
import { ChangePasswordView } from '~/features/ChangePassword';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';
import { cookieEnabledTestFeature } from '~/utils/helper';

import { usePrivateRoute } from './usePrivateRoute';
import { useSxStyles } from './useStyles';

export type PrivateRouteProps = RouteProps;

export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  component,
  children,
  render,
  ...restProps
}) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const { isAuthenticated, isLoading, hasData, isUserPasswordChangeRequired } =
    usePrivateRoute();
  useSessionInitialize({ isAuthenticated });

  // TODO: Remove when testing is over
  const isPasswordResetTestEnabled =
    cookieEnabledTestFeature('PASSWORD_RESET_UI2');

  return (
    <Route
      {...restProps}
      render={(props) => {
        if (!isAuthenticated) {
          if (isLoading) {
            return (
              <LoadingView sx={sxStyles.loading}>
                {t('authenticating')}
              </LoadingView>
            );
          } else if (!hasData) {
            return <Box sx={sxStyles.preRoutedContent}></Box>;
          }
          return (
            <Box sx={sxStyles.preRoutedContent}>
              <Alert severity={'error'}>{t('userNotAuthorized')}</Alert>
            </Box>
          );
        }

        return (
          <SelectedEmployeeContextRootProvider>
            <HandleCompanyPreferences />
            <HandleUserSettings />

            {isUserPasswordChangeRequired && isPasswordResetTestEnabled ? (
              <ChangePasswordView />
            ) : (
              (() => {
                if (children) {
                  if (children instanceof Function) {
                    return children(props);
                  }

                  return children;
                }

                if (component) {
                  return React.createElement(
                    component as React.ComponentType<RouteComponentProps>,
                    props,
                  );
                }

                return render ? render(props) : null;
              })()
            )}
          </SelectedEmployeeContextRootProvider>
        );
      }}
    />
  );
};
